<template>
  <div class="page-view">
    <BasePageHeader
      :title="isEdit ? $t('serviceHistory.editTitle') : $t('serviceHistory.newTitle')"
      @back="router.back()"
    >
      <template #actions>
        <button class="btn ghost small" @click="router.back()">{{ $t('common.cancel') }}</button>
        <button class="btn primary small" :disabled="saving || !isValid" @click="submit">
          {{ saving ? $t('common.saving') : (isEdit ? $t('common.save') : $t('common.create')) }}
        </button>
      </template>
    </BasePageHeader>

    <EmptyState v-if="loading" :message="$t('common.loading')" />
    <div v-else-if="loadError" class="state-empty error">{{ loadError }}</div>

    <template v-else>

      <!-- Basic details -->
      <div class="px-4 pt-5 pb-4 border-b border-[var(--color-border)]">
        <div class="section-label">{{ $t('serviceHistory.recordDetails') }}</div>

        <BaseFormField :label="$t('serviceHistory.titleLabel')" required>
          <BaseInput v-model="form.title" :placeholder="$t('ui.eGRsservicePumpeA')" />
        </BaseFormField>

        <BaseFormField :label="$t('serviceHistory.descriptionLabel')" required>
          <BaseTextarea v-model="form.description" placeholder="Hva ble gjort…" :rows="3" />
        </BaseFormField>

        <div class="form-row">
          <BaseFormField :label="$t('serviceHistory.serviceDateLabel')" required>
            <BaseInput v-model="form.service_date" type="date" />
          </BaseFormField>
          <BaseFormField :label="$t('serviceHistory.typeLabel')">
            <BaseSelect v-model="form.maintenance_type">
              <option value="">{{ $t('serviceHistory.typeNone') }}</option>
              <option v-for="type in MAINT_TYPES" :key="type.value" :value="type.value">{{ type.label }}</option>
            </BaseSelect>
          </BaseFormField>
        </div>

        <div class="form-row">
          <BaseFormField :label="$t('serviceHistory.statusLabel')">
            <BaseSelect v-model="form.status">
              <option v-for="(label, val) in STATUS_LABELS" :key="val" :value="val">{{ label }}</option>
            </BaseSelect>
          </BaseFormField>
          <BaseFormField :label="$t('serviceHistory.actualHoursLabel')">
            <BaseInput v-model="form.actual_hours" type="number" min="0" step="0.5" placeholder="0" />
          </BaseFormField>
        </div>
      </div>

      <!-- Items -->
      <div class="px-4 pt-5 pb-4 border-b border-[var(--color-border)]">
        <div class="section-label">{{ $t('serviceHistory.itemsSection') }}</div>

        <div v-if="form.items?.length" class="flex flex-wrap gap-1.5 mb-3">
          <span
            v-for="it in form.items" :key="it.id"
            class="badge badge-accent badge-pill flex items-center gap-1"
          >
            {{ it.label }}
            <button
              type="button"
              class="bg-transparent border-0 text-blue-300 cursor-pointer text-sm leading-none p-0 hover:text-red-400"
              @click="removeItem(it.id)"
            >×</button>
          </span>
        </div>
        <BaseFormField :label="$t('serviceHistory.searchItemsPlaceholder')">
          <SearchField
            v-model="itemSearch"
            :results="mappedItemResults"
            :loading="itemLoading"
            :debounce="300"
            search-key="svc-items"
            :placeholder="$t('serviceHistory.searchItemsPlaceholder')"
            @search="doItemSearch"
            @select="addItem($event.meta)"
          />
        </BaseFormField>
      </div>

      <!-- Findings & actions -->
      <div class="px-4 pt-5 pb-4 border-b border-[var(--color-border)]">
        <div class="section-label">{{ $t('serviceHistory.findingsActionsSection') }}</div>

        <BaseFormField :label="$t('serviceHistory.findingsInputLabel')">
          <BaseTextarea v-model="form.findings" placeholder="Funn underveis…" :rows="2" />
        </BaseFormField>

        <BaseFormField :label="$t('serviceHistory.actionsTakenLabel')">
          <BaseTextarea v-model="form.actions_taken" placeholder="Hva ble utbedret…" :rows="2" />
        </BaseFormField>

        <div class="form-row">
          <BaseFormField :label="$t('serviceHistory.partsReplacedLabel')">
            <BaseInput v-model="form.parts_replaced" placeholder="Del A, Del B…" />
          </BaseFormField>
          <BaseFormField :label="$t('serviceHistory.costLabel')">
            <BaseInput v-model="form.cost" type="number" min="0" step="0.01" placeholder="0.00" />
          </BaseFormField>
        </div>
      </div>

      <!-- Image upload -->
      <div class="px-4 pt-5 pb-4 border-b border-[var(--color-border)]">
        <div class="section-label">{{ $t('serviceHistory.imageLabel') }} <span class="text-[var(--color-muted)] normal-case font-normal">{{ $t('serviceHistory.imageOptional') }}</span></div>

        <!-- Existing image (edit mode) -->
        <div v-if="existingImageUrl && !newPhotoPreview" class="mb-3">
          <img
            :src="existingImageUrl"
            :alt="$t('serviceHistory.imageLabel')"
            class="max-h-40 w-full rounded-lg border border-[var(--color-border)] object-contain bg-black/20 cursor-zoom-in"
            @click="lightboxSrc = existingImageUrl"
          />
          <button
            type="button"
            class="mt-1.5 text-xs text-red-400 underline cursor-pointer bg-transparent border-0 p-0"
            @click="removeExistingImage"
          >{{ $t('serviceHistory.removeImage') }}</button>
        </div>

        <!-- New photo preview -->
        <div v-if="newPhotoPreview" class="relative mb-3">
          <img
            :src="newPhotoPreview"
            :alt="$t('serviceHistory.imageLabel')"
            class="max-h-40 w-full rounded-lg border border-[var(--color-border)] object-contain bg-black/20 cursor-zoom-in"
            @click="lightboxSrc = newPhotoPreview"
          />
          <button
            type="button"
            class="absolute top-2 right-2 bg-black/60 border-0 text-white rounded-md px-2 py-1 text-xs cursor-pointer"
            @click="clearNewPhoto"
          >{{ $t('common.remove') }}</button>
        </div>

        <!-- Upload trigger -->
        <div
          v-if="!newPhotoPreview"
          class="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[var(--color-border)] rounded-xl p-5 cursor-pointer text-[var(--color-muted)] text-sm hover:border-[var(--color-primary)] transition-colors"
          @click="fileInput?.click()"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="w-8 h-8">
            <rect x="2" y="7" width="20" height="15" rx="2"/>
            <path d="M16 3l-4 4-4-4"/>
            <circle cx="12" cy="14" r="3"/>
          </svg>
          <span>{{ existingImageUrl ? $t('serviceHistory.replaceImage') : $t('serviceHistory.uploadPhoto') }}</span>
        </div>
        <BaseInput
          ref="fileInput"
          type="file"
          accept="image/*"
          capture="environment"
          class="hidden"
          :aria-label="$t('serviceHistory.uploadPhoto')"
          @change="onFileChange"
        />
      </div>

      <!-- Error -->
      <div v-if="saveError" class="mx-4 mt-3 state-empty error">{{ saveError }}</div>

    </template>
  </div>

  <!-- Lightbox -->
  <Teleport to="body">
    <div
      v-if="lightboxSrc"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/85"
      @click="lightboxSrc = null"
    >
      <button
        class="absolute top-4 right-4 btn-icon z-10"
        @click.stop="lightboxSrc = null"
        :aria-label="$t('common.close')"
      >
        <MdiClose class="w-5 h-5" />
      </button>
      <img
        :src="lightboxSrc"
        class="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
        @click.stop
      />
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@/composables/useToast'
import {
  getServiceHistoryById,
  createServiceHistory, createServiceHistoryWithImage,
  updateServiceHistory, updateServiceHistoryWithImage,
  searchItemMetadata,
} from '@/api/api.js'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseTextarea from '@/components/base/BaseTextarea.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import SearchField from '@/components/search/SearchField.vue'
import MdiClose from '~icons/mdi/close'

const router = useRouter()
const route  = useRoute()
const toast  = useToast()
const { t }  = useI18n()

const id     = route.params.id
const isEdit = !!id

const MAINT_TYPES = computed(() => [
  { value: 'preventive',  label: t('serviceHistory.types.preventive') },
  { value: 'corrective',  label: t('serviceHistory.types.corrective') },
  { value: 'inspection',  label: t('serviceHistory.types.inspection') },
  { value: 'calibration', label: t('serviceHistory.types.calibration') },
])
const STATUS_LABELS = computed(() => ({
  pending:     t('serviceHistory.statuses.pending'),
  in_progress: t('serviceHistory.statuses.in_progress'),
  completed:   t('serviceHistory.statuses.completed'),
  failed:      t('serviceHistory.statuses.failed'),
}))

// ── form state ────────────────────────────────────────────────────────
const loading   = ref(isEdit)
const loadError = ref(null)
const saving    = ref(false)
const saveError = ref(null)

const form = ref({
  title:            '',
  description:      '',
  service_date:     new Date().toISOString().slice(0, 10),
  maintenance_type: '',
  status:           'completed',
  actual_hours:     '',
  cost:             '',
  items:            [],
  findings:         '',
  actions_taken:    '',
  parts_replaced:   '',
})

// ── image state ───────────────────────────────────────────────────────
const fileInput        = ref(null)
const newPhotoFile     = ref(null)
const newPhotoPreview  = ref(null)
const existingImageUrl = ref(null)
const clearImage       = ref(false)   // flag to tell API to clear existing image
const lightboxSrc      = ref(null)

function onFileChange(e) {
  const file = e.target.files?.[0]
  if (!file) return
  newPhotoFile.value = file
  newPhotoPreview.value = URL.createObjectURL(file)
  clearImage.value = false
}

function clearNewPhoto() {
  newPhotoFile.value    = null
  newPhotoPreview.value = null
  if (fileInput.value?.el) fileInput.value.el.value = ''
}

function removeExistingImage() {
  existingImageUrl.value = null
  clearImage.value = true
}

// ── item search ───────────────────────────────────────────────────────
const itemSearch  = ref('')
const itemResults = ref([])
const itemLoading = ref(false)

const mappedItemResults = computed(() =>
  itemResults.value.map(r => ({
    id: r.id,
    label: instanceLabel(r),
    sublabel: r.item?.name ?? '',
    meta: r,
  }))
)

async function doItemSearch(q) {
  if (!q) { itemResults.value = []; return }
  itemLoading.value = true
  try {
    const res = await searchItemMetadata(q, { page_size: 10 })
    itemResults.value = (res.results ?? res ?? []).filter(
      r => !(form.value.items ?? []).some(s => s.id === r.id)
    )
  } catch {
    itemResults.value = []
  } finally {
    itemLoading.value = false
  }
}

function addItem(inst) {
  if (form.value.items.some(i => i.id === inst.id)) return
  form.value.items.push({ id: inst.id, label: instanceLabel(inst) })
  itemSearch.value  = ''
  itemResults.value = []
}

function removeItem(itemId) {
  form.value.items = form.value.items.filter(i => i.id !== itemId)
}

function instanceLabel(inst) {
  if (!inst) return ''
  if (typeof inst === 'object') return inst.asset_tag ?? inst.serial_number ?? inst.item?.name ?? `#${inst.id}`
  return `#${inst}`
}

// ── load for edit ─────────────────────────────────────────────────────
onMounted(async () => {
  if (!isEdit) return
  try {
    const h = await getServiceHistoryById(id)
    form.value = {
      title:            h.title ?? '',
      description:      h.description ?? '',
      service_date:     h.service_date ?? '',
      maintenance_type: h.maintenance_type ?? '',
      status:           h.status ?? 'completed',
      actual_hours:     h.actual_hours ?? '',
      cost:             h.cost ?? '',
      items:            (h.item_metadata ?? []).map(i =>
        typeof i === 'object'
          ? { id: i.id, label: instanceLabel(i) }
          : { id: i, label: `#${i}` }
      ),
      findings:         h.findings ?? '',
      actions_taken:    h.actions_taken ?? '',
      parts_replaced:   h.parts_replaced ?? '',
    }
    existingImageUrl.value = h.image_url ?? null
  } catch (e) {
    loadError.value = e?.data?.detail ?? e?.message ?? t('serviceHistory.loadRecordFailed')
  } finally {
    loading.value = false
  }
})

// ── validation ────────────────────────────────────────────────────────
const isValid = computed(() =>
  form.value.title.trim() &&
  form.value.description.trim() &&
  form.value.service_date
)

// ── submit ────────────────────────────────────────────────────────────
async function submit() {
  if (saving.value || !isValid.value) return
  saving.value  = true
  saveError.value = null

  try {
    if (newPhotoFile.value) {
      // Use multipart if we have a new image
      const fd = new FormData()
      fd.append('title',       form.value.title.trim())
      fd.append('description', form.value.description.trim())
      fd.append('service_date', form.value.service_date)
      if (form.value.maintenance_type) fd.append('maintenance_type', form.value.maintenance_type)
      if (form.value.status)           fd.append('status', form.value.status)
      if (form.value.actual_hours !== '') fd.append('actual_hours', String(form.value.actual_hours))
      if (form.value.cost !== '')         fd.append('cost', String(form.value.cost))
      if (form.value.findings?.trim())    fd.append('findings', form.value.findings.trim())
      if (form.value.actions_taken?.trim()) fd.append('actions_taken', form.value.actions_taken.trim())
      if (form.value.parts_replaced?.trim()) fd.append('parts_replaced', form.value.parts_replaced.trim())
      form.value.items.forEach(i => fd.append('item_metadata', i.id))
      fd.append('image', newPhotoFile.value)

      if (isEdit) {
        await updateServiceHistoryWithImage(id, fd)
      } else {
        await createServiceHistoryWithImage(fd)
      }
    } else {
      // Plain JSON — possibly clear image on edit
      const payload = {
        title:         form.value.title.trim(),
        description:   form.value.description.trim(),
        service_date:  form.value.service_date,
        item_metadata: form.value.items.map(i => i.id),
      }
      if (form.value.maintenance_type)       payload.maintenance_type = form.value.maintenance_type
      if (form.value.status)                 payload.status           = form.value.status
      if (form.value.actual_hours !== '')    payload.actual_hours     = String(form.value.actual_hours)
      if (form.value.cost !== '')            payload.cost             = String(form.value.cost)
      if (form.value.findings?.trim())       payload.findings         = form.value.findings.trim()
      if (form.value.actions_taken?.trim())  payload.actions_taken    = form.value.actions_taken.trim()
      if (form.value.parts_replaced?.trim()) payload.parts_replaced   = form.value.parts_replaced.trim()
      if (clearImage.value)                  payload.image            = null

      if (isEdit) {
        await updateServiceHistory(id, payload)
      } else {
        await createServiceHistory(payload)
      }
    }

    toast.success(isEdit ? t('serviceHistory.updated') : t('serviceHistory.created'))
    router.push('/maintenance/history')
  } catch (e) {
    const d = e?.data
    if (d && typeof d === 'object') {
      const msgs = Object.entries(d)
        .filter(([k]) => k !== 'status_code')
        .flatMap(([, v]) => Array.isArray(v) ? v : [v])
        .filter(Boolean)
        .join(' ')
      saveError.value = msgs || e?.message || t('serviceHistory.saveFailed')
    } else {
      saveError.value = e?.message ?? t('serviceHistory.saveFailed')
    }
  } finally {
    saving.value = false
  }
}
</script>
